<template>
  <div class="flex flex-col min-h-0" data-testid="ai-conversation-list">
    <!-- New chat -->
    <button type="button"
      class="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-lg text-primary hover:bg-primary/10 transition-colors shrink-0"
      data-testid="ai-new-chat"
      @click="emit('new')">
      <i class="pi pi-plus-circle"></i>
      New chat
    </button>

    <div class="border-t border-gray-100 my-2 shrink-0"></div>

    <!-- Loading -->
    <div v-if="list.loading.value && !list.conversations.value.length"
      class="flex items-center gap-2 px-3 py-4 text-sm text-gray-400" data-testid="ai-list-loading">
      <i class="pi pi-spin pi-spinner"></i>
      Loading chats…
    </div>

    <!-- Load failure: inline, non-blocking (composer stays usable) -->
    <div v-else-if="list.error.value"
      class="flex items-start gap-2 mx-1 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs"
      data-testid="ai-list-error">
      <i class="pi pi-exclamation-triangle mt-0.5"></i>
      <div class="flex-1">
        <p>{{ list.error.value }}</p>
        <button type="button" class="underline mt-1" data-testid="ai-list-retry" @click="emit('refresh')">
          Try again
        </button>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="list.isEmpty.value" class="px-3 py-6 text-center" data-testid="ai-list-empty">
      <i class="pi pi-comments text-2xl text-gray-300 mb-2 block"></i>
      <p class="text-sm text-gray-500">No chats yet</p>
      <p class="text-xs text-gray-400 mt-0.5">Your conversations will show up here.</p>
    </div>

    <!-- The list -->
    <ul v-else class="flex-1 min-h-0 overflow-y-auto space-y-0.5 pr-0.5">
      <li v-for="item in list.conversations.value" :key="item.id">
        <div
          class="group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
          :class="item.id === activeId ? 'bg-primary/10 text-primary' : 'hover:bg-gray-50 text-gray-700'"
          :data-testid="`ai-conversation-${item.id}`"
          @click="emit('select', item.id)">
          <i class="pi pi-comment text-sm shrink-0 opacity-70"></i>
          <div class="flex-1 min-w-0">
            <p class="text-sm truncate" :title="item.title">{{ item.title || 'Untitled chat' }}</p>
            <p class="text-xs text-gray-400">{{ relativeDate(item.updated_at) }}</p>
          </div>
          <button type="button"
            class="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-red-600 transition-opacity shrink-0 p-1"
            :aria-label="`Delete ${item.title || 'chat'}`"
            :data-testid="`ai-delete-${item.id}`"
            @click.stop="confirmDelete(item)">
            <i class="pi pi-trash text-xs"></i>
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { formatDistanceToNow } from 'date-fns';
import { useConfirm } from 'primevue/useconfirm';
import type { UseAiConversationsReturn } from '@/composables/useAiConversations';
import type { AiConversationSummary } from '@/services/aiConversations';

/**
 * Browse UI for stored chats — shared by the floating panel (header menu) and
 * the full page (sidebar). Presentation only: the host owns selection and
 * passes the list state in, so both hosts behave identically.
 *
 * Deletion confirms through PrimeVue's ConfirmDialog (globally mounted in
 * App.vue) — never window.confirm, which blocks browser automation.
 */
const props = defineProps<{
  list: UseAiConversationsReturn;
  /** Highlights the conversation currently open in the transcript. */
  activeId?: number | null;
}>();

const emit = defineEmits<{
  (e: 'select', id: number): void;
  (e: 'new'): void;
  (e: 'refresh'): void;
  (e: 'deleted', id: number): void;
}>();

const confirm = useConfirm();

const relativeDate = (iso: string) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true });
};

const confirmDelete = (item: AiConversationSummary) => {
  confirm.require({
    message: `Delete "${item.title || 'this chat'}"? This cannot be undone.`,
    header: 'Delete chat',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Delete',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async() => {
      const ok = await props.list.remove(item.id);
      if (ok) emit('deleted', item.id);
    },
  });
};
</script>
